import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { usePrefs } from "@/lib/context";
import { fetcher } from "@/lib/hooks";
import { BackupCodes } from "@/components/MfaStep";

export function SecurityPanel() {
  const { t, fdatetime } = usePrefs();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["mfa-status"], queryFn: () => fetcher("/auth/mfa/status") });
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  if (!data) return null;
  const regen = async (e) => {
    e.preventDefault();
    try { const { data: d } = await api.post("/auth/mfa/backup-codes", { code }); setCodes(d.backup_codes); setCode(""); qc.invalidateQueries({ queryKey: ["mfa-status"] }); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <section className="max-w-xl space-y-4 rounded-sm border border-slate-200 bg-white p-5" data-testid="security-panel">
      <div className={`flex items-center gap-2 font-semibold ${data.enabled ? "text-emerald-700" : "text-amber-700"}`} data-testid="mfa-status">
        {data.enabled ? <ShieldCheck size={18} /> : <ShieldOff size={18} />}{data.enabled ? t("mfaEnabled") : t("mfaOff")}
      </div>
      {data.enabled && <p className="text-sm text-slate-600"><span className="font-data font-bold" data-testid="mfa-backup-left">{data.backup_left}</span> {t("backupLeft")}{data.enabled_at ? ` · ${fdatetime(data.enabled_at)}` : ""}</p>}
      {data.enabled && !codes && (
        <form onSubmit={regen} className="flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("currentCode")} maxLength={6} inputMode="numeric" required className="rounded-sm font-data" data-testid="mfa-regen-code-input" />
          <Button type="submit" className="shrink-0 rounded-sm bg-[#0F172A]" data-testid="mfa-regen-button">{t("regenBackup")}</Button>
        </form>
      )}
      {codes && <BackupCodes codes={codes} onDone={() => setCodes(null)} />}
    </section>
  );
}

export function MfaResetButton({ u }) {
  const { t } = usePrefs();
  const qc = useQueryClient();
  if (!["admin", "supervision"].includes(u.role) || !u.mfa_enabled) return null;
  const reset = async () => {
    if (!window.confirm(`${t("mfaReset")} — ${u.name} ?`)) return;
    try { await api.post(`/auth/mfa/reset/${u.id}`); toast.success(t("mfaResetDone")); qc.invalidateQueries({ queryKey: ["admin-users"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  return <Button size="sm" variant="outline" onClick={reset} className="mt-2 h-7 rounded-sm text-xs" data-testid={`mfa-reset-${u.id}`}><ShieldOff size={12} className="me-1" />{t("mfaReset")}</Button>;
}
